import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCountries } from 'react-phone-number-input/input';
import labelsEn from 'react-phone-number-input/locale/en.json';
import labelsFr from 'react-phone-number-input/locale/fr.json';
import { Check, ChevronsUpDown, Globe } from 'lucide-react';

import { Button } from '../ui/button';
import {
    Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';

/**
 * <CountrySelect> — searchable country dropdown, shared between admin
 * (Customers, Suppliers, Employees, …) and the B2B portal.
 *
 * • Stores ISO 3166-1 alpha-2 codes ("FR", "BE") as the canonical
 *   value; renders the localised country name so the user sees the
 *   familiar label.
 * • Country roster pulled from `react-phone-number-input` so it stays
 *   aligned with the phone-input country roster (same source of
 *   truth for both selectors).
 * • Translation labels: uses "en" or "fr" from react-phone-number-input
 *   according to the current i18next language — works with any
 *   translation namespace configured in the host app.
 *
 * Props
 *   value          – ISO code or '' when nothing picked
 *   onChange       – (isoCode, humanLabel) => void
 *   disabled       – bool
 *   placeholder    – text shown when value is empty (default "Select country")
 *   searchPlaceholder – search box placeholder
 *   emptyLabel     – text shown when no country matches the search
 *   testid         – data-testid for the trigger; children get suffixed testids
 *   className      – extra Tailwind classes on the trigger button
 */
export const CountrySelect = ({
    value,
    onChange,
    disabled = false,
    placeholder = 'Select country',
    searchPlaceholder = 'Search country…',
    emptyLabel = 'No country found.',
    testid = 'country-select',
    className = '',
}) => {
    const { i18n } = useTranslation();
    const labels = (i18n.resolvedLanguage || 'en').startsWith('fr') ? labelsFr : labelsEn;
    const [open, setOpen] = useState(false);

    const options = useMemo(() => {
        return getCountries()
            .map((code) => ({ code, label: labels[code] || code }))
            .sort((a, b) => a.label.localeCompare(b.label, i18n.resolvedLanguage));
    }, [labels, i18n.resolvedLanguage]);

    const currentLabel = value ? (labels[value] || value) : '';

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    data-testid={testid}
                    className={cn('w-full justify-between font-normal', className)}
                >
                    <span className="flex items-center gap-2 truncate">
                        <Globe size={14} className="text-gray-400" />
                        {currentLabel || <span className="text-gray-400">{placeholder}</span>}
                    </span>
                    <ChevronsUpDown size={14} className="opacity-50 shrink-0" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command>
                    <CommandInput
                        placeholder={searchPlaceholder}
                        data-testid={`${testid}-search`}
                    />
                    <CommandList className="max-h-[260px]">
                        <CommandEmpty>{emptyLabel}</CommandEmpty>
                        <CommandGroup>
                            {options.map((opt) => (
                                <CommandItem
                                    key={opt.code}
                                    value={`${opt.label} ${opt.code}`}
                                    onSelect={() => {
                                        onChange(opt.code, opt.label);
                                        setOpen(false);
                                    }}
                                    data-testid={`${testid}-option-${opt.code}`}
                                    className="cursor-pointer"
                                >
                                    <Check
                                        size={14}
                                        className={cn(
                                            'mr-2',
                                            value === opt.code ? 'opacity-100' : 'opacity-0',
                                        )}
                                    />
                                    <span className="font-mono text-xs text-gray-400 w-7">
                                        {opt.code}
                                    </span>
                                    <span>{opt.label}</span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export default CountrySelect;
